import { useEffect, useState } from "react";
import styled from "styled-components";
import { Form, Select } from "antd";
import { MdTune } from "react-icons/md";
import { FiltrosAvancado } from "../../../interfaces/Planejamento";
import { useFilters } from "core/features/filters/store";
import useFiltro from "../../../hooks/useFiltro";
import ModalDetalheTalhao from "../components/ModalDetalheTalhao";
import useRelatorioContext from "../../../hooks/useRelatorioContext";

type TipoPlanejamento = "PlanejamentoInicial" | "PlanejamentoAjustado";

const TOLERANCIA_MIN = 0;
const TOLERANCIA_MAX = 365;

type Props = {
    filtrosAvancados?: FiltrosAvancado;
};

function FiltroRelatorioAderencia({ filtrosAvancados }: Props) {
    const { Item, useForm } = Form;
    const [form] = useForm();
    const { filters, updateField } = useFilters();
    const { filtersAdvanced, unidade } = filters;
    const { filtroExecucaoOPeracional, update } = useFiltro();
    const [tipoPlanejamento, setTipoPlanejamento] = useState<TipoPlanejamento>("PlanejamentoInicial");
    const [tolerancia, setTolerancia] = useState(0);
    const { setToleranciaEmDias, setTipoPlanejamento: setTipo } = useRelatorioContext();

    function clampTolerancia(value: number) {
        return Math.min(TOLERANCIA_MAX, Math.max(TOLERANCIA_MIN, value));
    }

    function handleToleranciaChange(value: number) {
        const clamped = clampTolerancia(value);
        setTolerancia(clamped);
        setToleranciaEmDias(clamped);
    }

    function handleToleranciaInputChange(raw: string) {
        const digitsOnly = raw.replace(/\D/g, "");
        if (digitsOnly === "") {
            handleToleranciaChange(0);
            return;
        }
        handleToleranciaChange(Number(digitsOnly));
    }

    function getDefaultValue() {
        const filtro = filtersAdvanced?.safraPeriodo;
        const filtros = filtrosAvancados?.safraPeriodos;
        if (!filtros?.some(f => f.safraPeriodoId === filtro)) {
            form.setFieldsValue({ periodoProducao: undefined });
            return undefined;
        }
        form.setFieldsValue({ periodoProducao: filtro });
        return filtro;
    }

    useEffect(() => {
        let obj = {
            ...filtroExecucaoOPeracional,
            statusSemaforo: undefined,
            safraPeriodos: [filtersAdvanced?.safraPeriodo],
            tipoAderencia: {
                Tipo: tipoPlanejamento,
                ToleranciaEmDias: tolerancia
            },
        };
        if (filtersAdvanced?.classeOperacao) {
            const itemSelectedOperacao = filtrosAvancados?.operacaoClasses
                ?.map((item: any) => ({
                    operacaoClasseId: item.operacaoClasseId,
                    cor: item.color
                }))
                ?.filter(({ operacaoClasseId }) => filtersAdvanced?.classeOperacao?.includes(operacaoClasseId));
            obj.operacaoClasses = itemSelectedOperacao;
        }

        if (filtersAdvanced?.operacao) {
            const itemSelectedOperacao = filtrosAvancados?.operacoes
                ?.map((item: any) => ({
                    operacao: item.descricao,
                    operacaoId: item.operacaoId,
                    cor: item.cor
                }))
                ?.find(({ operacaoId }) => operacaoId === filtersAdvanced?.operacao);
            if (itemSelectedOperacao)
                obj.operacoes = [itemSelectedOperacao];
        }

        if (filtrosAvancados) update(obj);
    }, [tipoPlanejamento, tolerancia, filtrosAvancados, filtersAdvanced?.safraPeriodo, filtersAdvanced?.classeOperacao, filtersAdvanced?.operacao]);

    useEffect(() => {
        form.setFieldsValue({ periodoProducao: '' });
    }, [unidade]);

    return (
        <Container>
            <Wrapper>
                <Form form={form} layout="vertical">
                    <Wrap>
                        <Item
                            label="Período de Produção"
                            name="periodoProducao"
                            rules={[{ required: true, message: "Período de Produção é obrigatório" }]}
                        >
                            <SelectAntd
                                onChange={(dataValue: any) => {
                                    updateField("filtersAdvanced", {
                                        ...filtersAdvanced,
                                        safraPeriodo: dataValue
                                    });
                                }}
                                showSearch
                                placeholder="Período de Produção"
                                optionFilterProp="label"
                                defaultValue={getDefaultValue()}
                                options={(filtrosAvancados?.safraPeriodos || [])
                                    .filter((f) =>
                                        filtersAdvanced?.cultura
                                            ? f.ocupacaoId === filtersAdvanced.cultura
                                            : true
                                    )
                                    .map((item: any) => ({
                                        label: item.nome,
                                        value: item.safraPeriodoId,
                                    }))
                                }
                            />
                        </Item>
                    </Wrap>
                    <Wrap>
                        <Item
                            label="Classe de Operação"
                            name="classeOperacao"
                        >
                            <SelectAntd
                                allowClear
                                showSearch
                                mode="multiple"
                                maxTagCount={2}
                                maxTagPlaceholder={(omittedValues) => <span>+{omittedValues.length}</span>}
                                placeholder="Classe de Operação"
                                optionFilterProp="label"
                                onChange={(dataValue: any) => {
                                    let payload: any = {
                                        ...filtersAdvanced,
                                    };
                                    const has = filtrosAvancados?.operacoes?.filter(operacao =>
                                        filtersAdvanced?.classeOperacao?.includes(operacao.operacaoClasseId)
                                        && operacao.operacaoId === form.getFieldValue('operacao')
                                    );

                                    if (filtersAdvanced?.operacao && has?.length === 0) {
                                        payload.operacao = ""

                                    }

                                    payload = {
                                        ...payload,
                                        classeOperacao: dataValue,
                                    }

                                    updateField("filtersAdvanced", payload);
                                    update({
                                        ...filtroExecucaoOPeracional,
                                        operacoes: [],
                                        operacaoClasses: [],
                                    });
                                }}
                                defaultValue={filtersAdvanced?.classeOperacao}
                                options={(filtrosAvancados?.operacaoClasses || [])?.map((item: any) => ({
                                    label: item.descricao,
                                    value: item.operacaoClasseId,
                                }))}
                            />
                        </Item>
                    </Wrap>
                    <ControlSection>
                        <ControlTitle>
                            <MdTune size={18} />
                            Controles de Análise
                        </ControlTitle>

                        <PlanningTypeGroup>
                            <PlanningTypeLabel>Tipo de Planejamento</PlanningTypeLabel>
                            <RadioGroup>
                                <RadioOption>
                                    <input
                                        type="radio"
                                        id="planejamento-inicial"
                                        name="tipo-planejamento"
                                        checked={tipoPlanejamento === "PlanejamentoInicial"}
                                        onChange={() => {
                                            setTipo(1);
                                            setTipoPlanejamento("PlanejamentoInicial")
                                        }}
                                    />
                                    <label htmlFor="planejamento-inicial">Planejamento Inicial</label>
                                </RadioOption>
                                <RadioOption>
                                    <input
                                        type="radio"
                                        id="planejamento-ajustado"
                                        name="tipo-planejamento"
                                        checked={tipoPlanejamento === "PlanejamentoAjustado"}
                                        onChange={() => {
                                            setTipo(2);
                                            setTipoPlanejamento("PlanejamentoAjustado")
                                        }}
                                    />
                                    <label htmlFor="planejamento-ajustado">Planejamento Ajustado</label>
                                </RadioOption>
                            </RadioGroup>
                        </PlanningTypeGroup>

                        <ToleranceGroup>
                            <ToleranceLabel>Tolerância de Divergência</ToleranceLabel>
                            <ToleranceControl>
                                <ToleranceButton
                                    type="button"
                                    onClick={() => handleToleranciaChange(tolerancia - 1)}
                                    disabled={tolerancia <= TOLERANCIA_MIN}
                                >-</ToleranceButton>
                                <ToleranceInput
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={tolerancia}
                                    onFocus={(event) => event.target.select()}
                                    onChange={(event) => handleToleranciaInputChange(event.target.value)}
                                />
                                <ToleranceButton
                                    type="button"
                                    onClick={() => handleToleranciaChange(tolerancia + 1)}
                                    disabled={tolerancia >= TOLERANCIA_MAX}
                                >+</ToleranceButton>
                            </ToleranceControl>
                            <ToleranceHint>
                                Limite de diferença aceitável entre planejado e executado
                            </ToleranceHint>
                            <ModalDetalheTalhao />
                        </ToleranceGroup>
                    </ControlSection>
                </Form>
            </Wrapper>
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    padding: 8px 16px;
  
    form {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 12px;
        border-bottom: 1px solid #e0e0e0;
    }
`;

const Wrapper = styled.div`
    width: 100%;

    .ant-form-item-required::before {
      content: none !important;
    }

  .ant-form-item-required::after {
    display: inline-block !important;
    margin-left: 4px !important;
    color: red;
    font-size: 14px !important;
    line-height: 1 !important;
    content: "*" !important;
  }
`;

const Wrap = styled.div`
    width: 100%;

    .ant-form-item {
      margin-bottom: 0;
    }

    .ant-col {
      padding: 0;
    }
`;

export const SelectAntd = styled(Select)`
  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    padding: 4px 11px !important;
  }
`;

const ControlSection = styled.div`
    padding: 20px;
    margin: 0 -16px;
    width: calc(100% + 32px);
    border-top: 1px solid #e0e6ed;
    border-bottom: 1px solid #e0e6ed;
`;

const ControlTitle = styled.h3`
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PlanningTypeGroup = styled.div`
    margin-bottom: 20px;
`;

const PlanningTypeLabel = styled.span`
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
    margin-bottom: 10px;
`;

const RadioGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const RadioOption = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    input[type="radio"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: #2196f3;
        flex-shrink: 0;
    }

    label {
        font-size: 14px;
        cursor: pointer;
        color: #334155;
        user-select: none;
    }
`;

const ToleranceGroup = styled.div`
    margin-top: 20px;
`;

const ToleranceLabel = styled.span`
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
    margin-bottom: 12px;
`;

const ToleranceControl = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ToleranceButton = styled.button`
    width: 36px;
    height: 36px;
    border: 1px solid #cbd5e1;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #475569;
    font-size: 20px;
    font-weight: 600;
    transition: all 0.2s;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #2196f3;
        color: #2196f3;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const ToleranceInput = styled.input`
    flex: 1;
    text-align: center;
    padding: 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    background: #f8fafc;
    min-width: 0;

    &:focus {
        outline: none;
        border-color: #2196f3;
        background: white;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

const ToleranceHint = styled.span`
    display: block;
    font-size: 11px;
    color: #94a3b8;
    margin-top: 8px;
`;

export default FiltroRelatorioAderencia;